/**
 * Quantities — how much of a thing, exactly.
 *
 * Same discipline as money, for the same reason: `Number("0.1") * 1000` is
 * 100.00000000000001, and 1.5 kg of apples bought forty times should add up to
 * 60 kg and not 59.999999. Quantity is stored as an integer number of
 * thousandths ("milli"), which covers grams, millilitres and the fractional
 * pieces a receipt ever prints, and nothing here ever touches a float.
 *
 * Three decimal places is a deliberate ceiling. Anything finer is measurement
 * noise on a supermarket receipt, and a quantity typed with four decimals is
 * far more likely to be a typo than a real purchase — so it is rejected rather
 * than rounded away.
 */
import { InvalidAmountError } from './errors.js';
/** Thousandths per whole unit. */
export const QUANTITY_SCALE = 1000n;
export const QUANTITY_DECIMALS = 3;
/**
 * Parse "1", "1.5", "0.250" into thousandths. Rejects negatives: a line on a
 * receipt is something you took home, and a refund is an entry of its own, not
 * a negative apple.
 */
export function parseQuantity(input) {
    const trimmed = input.trim();
    const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
    if (!match)
        throw new InvalidAmountError(`"${input}" is not a valid quantity`);
    const [, whole = '0', fraction = ''] = match;
    if (fraction.length > QUANTITY_DECIMALS)
        throw new InvalidAmountError(`A quantity may have at most ${QUANTITY_DECIMALS} decimal places; "${input}" has ${fraction.length}`);
    const value = BigInt(whole + fraction.padEnd(QUANTITY_DECIMALS, '0'));
    if (value === 0n)
        throw new InvalidAmountError('A quantity must be greater than zero');
    return value;
}
/**
 * Render thousandths back as the shortest honest decimal: 1000 → "1",
 * 1500 → "1.5", 250 → "0.25". Trailing zeros are dropped because "1.500 pcs"
 * reads like a precision that was never claimed.
 */
export function formatQuantity(milli) {
    const negative = milli < 0n;
    const digits = (negative ? -milli : milli).toString().padStart(QUANTITY_DECIMALS + 1, '0');
    const whole = digits.slice(0, digits.length - QUANTITY_DECIMALS);
    const fraction = digits.slice(digits.length - QUANTITY_DECIMALS).replace(/0+$/, '');
    return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}
/** Sum quantities. Only meaningful within one unit — see `canSumUnits`. */
export function sumQuantities(values) {
    return values.reduce((total, value) => total + value, 0n);
}
/**
 * Whether a running total across these units means anything.
 *
 * Buying 2 litres of milk and 1 piece of milk is a real thing that happens
 * (one week by the bottle, one week by the carton), and adding them gives 3 of
 * nothing. The Items screen shows a total only when every purchase used the
 * same unit, and otherwise shows the count of purchases — which is always true.
 */
export function canSumUnits(units) {
    return units.length > 0 && units.every((unit) => unit === units[0]);
}
//# sourceMappingURL=quantity.js.map